// Meeting Polls: Let invitees vote on preferred meeting times
import React, { useState, useEffect } from 'react';
import { schedulingApi } from '../../services/scheduling-api';
import './SchedulingStyles.css';

interface Poll {
  id: number;
  pollUid: string;
  title: string;
  description?: string;
  durationMinutes: number;
  proposedTimes: string[];
  status: 'open' | 'closed' | 'confirmed';
  confirmedTime?: string;
  deadline?: string;
  createdAt: string;
  pollUrl: string;
}

interface PollFormData {
  title: string;
  description: string;
  durationMinutes: number;
  proposedTimes: string[];
  deadline: string;
  allowComments: boolean;
  requireAllVotes: boolean;
  locationType: string;
}

export const MeetingPolls: React.FC = () => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<PollFormData>({
    title: '',
    description: '',
    durationMinutes: 30,
    proposedTimes: [],
    deadline: '',
    allowComments: true,
    requireAllVotes: false,
    locationType: 'google_meet'
  });
  const [newTimeSlot, setNewTimeSlot] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedPollId, setCopiedPollId] = useState<string | null>(null);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      const response = await schedulingApi.get('/polls/');
      setPolls(response.data.polls || []);
    } catch (err) {
      console.error('Failed to fetch polls:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePoll = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (formData.proposedTimes.length < 2) {
      setError('At least 2 proposed times required');
      return;
    }

    try {
      const response = await schedulingApi.post('/polls/', {
        title: formData.title,
        description: formData.description,
        durationMinutes: formData.durationMinutes,
        proposedTimes: formData.proposedTimes,
        deadline: formData.deadline || null,
        allowComments: formData.allowComments,
        requireAllVotes: formData.requireAllVotes,
        locationType: formData.locationType
      });

      setSuccess(`Poll created! Share this link: ${response.data.pollUrl}`);
      setShowCreateModal(false);
      resetForm();
      fetchPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create poll');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      durationMinutes: 30,
      proposedTimes: [],
      deadline: '',
      allowComments: true,
      requireAllVotes: false,
      locationType: 'google_meet'
    });
    setNewTimeSlot('');
  };

  const addTimeSlot = () => {
    if (!newTimeSlot) return;
    const isoTime = new Date(newTimeSlot).toISOString();
    if (!formData.proposedTimes.includes(isoTime)) {
      setFormData({
        ...formData,
        proposedTimes: [...formData.proposedTimes, isoTime].sort()
      });
    }
    setNewTimeSlot('');
  };

  const removeTimeSlot = (time: string) => {
    setFormData({
      ...formData,
      proposedTimes: formData.proposedTimes.filter(t => t !== time)
    });
  };

  const handleConfirmPoll = async (pollUid: string, time: string) => {
    try {
      await schedulingApi.post(`/polls/${pollUid}/confirm`, {
        confirmedTime: time
      });
      setSuccess('Meeting time confirmed! Notifications sent to participants.');
      fetchPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm poll');
    }
  };

  const handleClosePoll = async (pollUid: string) => {
    try {
      await schedulingApi.post(`/polls/${pollUid}/close`);
      setSuccess('Poll closed');
      fetchPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close poll');
    }
  };

  const handleDeletePoll = async (pollUid: string) => {
    if (!window.confirm('Are you sure you want to delete this poll?')) return;

    try {
      await schedulingApi.delete(`/polls/${pollUid}`);
      setSuccess('Poll deleted');
      fetchPolls();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete poll');
    }
  };

  const copyPollLink = (pollUrl: string, pollUid: string) => {
    navigator.clipboard.writeText(pollUrl);
    setCopiedPollId(pollUid);
    setTimeout(() => setCopiedPollId(null), 2000);
  };

  const formatDateTime = (iso: string) => {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string }> = {
      open: { bg: '#dcfce7', text: '#166534' },
      closed: { bg: '#fef3c7', text: '#92400e' },
      confirmed: { bg: '#dbeafe', text: '#1e40af' }
    };
    const style = styles[status] || styles.open;
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: style.bg,
        color: style.text
      }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div className="scheduling-loading">Loading polls...</div>;
  }

  return (
    <div className="scheduling-section">
      <div className="scheduling-header">
        <h2>Meeting Polls</h2>
        <p>Let participants vote on their preferred meeting times</p>
        <button
          className="scheduling-btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create Poll
        </button>
      </div>

      {error && (
        <div className="scheduling-alert scheduling-alert-error">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="scheduling-alert scheduling-alert-success">
          {success}
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Polls List */}
      <div className="scheduling-list">
        {polls.length === 0 ? (
          <div className="scheduling-empty">
            <p>No polls created yet</p>
            <p>Create a poll to let participants vote on meeting times</p>
          </div>
        ) : (
          polls.map(poll => (
            <div key={poll.pollUid} className="scheduling-card">
              <div className="scheduling-card-header">
                <h3>{poll.title}</h3>
                {getStatusBadge(poll.status)}
              </div>

              {poll.description && (
                <p className="scheduling-card-description">{poll.description}</p>
              )}

              <div className="scheduling-card-meta">
                <span>{poll.durationMinutes} min</span>
                <span>{poll.proposedTimes.length} time options</span>
                {poll.deadline && (
                  <span>Deadline: {formatDateTime(poll.deadline)}</span>
                )}
              </div>

              {poll.status === 'confirmed' && poll.confirmedTime && (
                <div className="scheduling-confirmed-time">
                  Confirmed: {formatDateTime(poll.confirmedTime)}
                </div>
              )}

              <div className="scheduling-card-actions">
                <button
                  className="scheduling-btn-secondary"
                  onClick={() => copyPollLink(poll.pollUrl, poll.pollUid)}
                >
                  {copiedPollId === poll.pollUid ? 'Copied!' : 'Copy Link'}
                </button>

                {poll.status === 'open' && (
                  <>
                    <button
                      className="scheduling-btn-secondary"
                      onClick={() => handleClosePoll(poll.pollUid)}
                    >
                      Close Poll
                    </button>
                    <button
                      className="scheduling-btn-primary"
                      onClick={() => {
                        const time = poll.proposedTimes[0];
                        if (window.confirm(`Confirm meeting for ${formatDateTime(time)}?`)) {
                          handleConfirmPoll(poll.pollUid, time);
                        }
                      }}
                    >
                      Confirm Time
                    </button>
                  </>
                )}

                <button
                  className="scheduling-btn-danger"
                  onClick={() => handleDeletePoll(poll.pollUid)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Poll Modal */}
      {showCreateModal && (
        <div className="scheduling-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="scheduling-modal" onClick={e => e.stopPropagation()}>
            <div className="scheduling-modal-header">
              <h2>Create Meeting Poll</h2>
              <button
                className="scheduling-modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreatePoll}>
              <div className="scheduling-form-group">
                <label>Poll Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Team sync, Project kickoff, etc."
                />
              </div>

              <div className="scheduling-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add context about this meeting..."
                  rows={3}
                />
              </div>

              <div className="scheduling-form-row">
                <div className="scheduling-form-group">
                  <label>Duration</label>
                  <select
                    value={formData.durationMinutes}
                    onChange={e => setFormData({ ...formData, durationMinutes: Number(e.target.value) })}
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>

                <div className="scheduling-form-group">
                  <label>Location</label>
                  <select
                    value={formData.locationType}
                    onChange={e => setFormData({ ...formData, locationType: e.target.value })}
                  >
                    <option value="google_meet">Google Meet</option>
                    <option value="zoom">Zoom</option>
                    <option value="teams">Microsoft Teams</option>
                    <option value="phone">Phone Call</option>
                    <option value="in_person">In Person</option>
                  </select>
                </div>
              </div>

              <div className="scheduling-form-group">
                <label>Voting Deadline (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.deadline}
                  onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                />
              </div>

              <div className="scheduling-form-group">
                <label>Proposed Times * (at least 2)</label>
                <div className="scheduling-time-slots">
                  {formData.proposedTimes.map(time => (
                    <div key={time} className="scheduling-time-slot">
                      <span>{formatDateTime(time)}</span>
                      <button
                        type="button"
                        onClick={() => removeTimeSlot(time)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="scheduling-add-time">
                  <input
                    type="datetime-local"
                    value={newTimeSlot}
                    onChange={e => setNewTimeSlot(e.target.value)}
                  />
                  <button
                    type="button"
                    className="scheduling-btn-secondary"
                    onClick={addTimeSlot}
                  >
                    Add Time
                  </button>
                </div>
              </div>

              <div className="scheduling-form-group">
                <label className="scheduling-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.allowComments}
                    onChange={e => setFormData({ ...formData, allowComments: e.target.checked })}
                  />
                  Allow participants to leave comments
                </label>
              </div>

              <div className="scheduling-form-group">
                <label className="scheduling-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.requireAllVotes}
                    onChange={e => setFormData({ ...formData, requireAllVotes: e.target.checked })}
                  />
                  Require all invitees to vote before confirming
                </label>
              </div>

              <div className="scheduling-modal-actions">
                <button
                  type="button"
                  className="scheduling-btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="scheduling-btn-primary">
                  Create Poll
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Public Poll Voting Page
export const PublicPollPage: React.FC<{ pollUid: string }> = ({ pollUid }) => {
  const [poll, setPoll] = useState<any>(null);
  const [host, setHost] = useState<any>(null);
  const [voteSummary, setVoteSummary] = useState<Record<string, Record<string, number>>>({});
  const [voters, setVoters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [voterName, setVoterName] = useState('');
  const [voterEmail, setVoterEmail] = useState('');
  const [votes, setVotes] = useState<Record<string, string>>({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPoll();
  }, [pollUid]);

  const fetchPoll = async () => {
    try {
      const response = await schedulingApi.get(`/polls/public/${pollUid}`);
      setPoll(response.data.poll);
      setHost(response.data.host);
      setVoteSummary(response.data.voteSummary || {});
      setVoters(response.data.voters || []);

      // Initialize votes
      const initialVotes: Record<string, string> = {};
      response.data.poll.proposedTimes.forEach((time: string) => {
        initialVotes[time] = 'no';
      });
      setVotes(initialVotes);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load poll');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitVote = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await schedulingApi.post(`/polls/public/${pollUid}/vote`, {
        voterName,
        voterEmail,
        votes,
        comment: poll.allowComments ? comment : undefined
      });
      setSuccess('Your vote has been submitted!');
      fetchPoll(); // Refresh to show updated votes
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (iso: string) => {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="scheduling-loading">Loading poll...</div>;
  }

  if (error && !poll) {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-error-page">
          <h2>Poll Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (poll.status === 'confirmed') {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-confirmed-page">
          <h2>{poll.title}</h2>
          <p>This meeting has been confirmed!</p>
          <div className="scheduling-confirmed-details">
            <strong>When:</strong> {formatDateTime(poll.confirmedTime)}
          </div>
          <p>Check your email for calendar invite and meeting link.</p>
        </div>
      </div>
    );
  }

  if (poll.status === 'closed') {
    return (
      <div className="scheduling-public-page">
        <div className="scheduling-closed-page">
          <h2>{poll.title}</h2>
          <p>This poll has been closed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scheduling-public-page">
      <div className="scheduling-poll-container">
        <div className="scheduling-poll-header">
          {host?.avatarUrl && (
            <img src={host.avatarUrl} alt={host.displayName} className="scheduling-host-avatar" />
          )}
          <h1>{poll.title}</h1>
          {poll.description && <p>{poll.description}</p>}
          <div className="scheduling-poll-meta">
            <span>Hosted by {host?.displayName || 'Host'}</span>
            <span>{poll.durationMinutes} min meeting</span>
          </div>
          {poll.deadline && (
            <p className="scheduling-poll-deadline">
              Voting closes: {formatDateTime(poll.deadline)}
            </p>
          )}
        </div>

        {error && (
          <div className="scheduling-alert scheduling-alert-error">{error}</div>
        )}

        {success ? (
          <div className="scheduling-success-message">
            <h3>Thank you!</h3>
            <p>{success}</p>
            <p>You can update your vote by submitting again with the same email.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmitVote}>
            <div className="scheduling-form-row">
              <div className="scheduling-form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={voterName}
                  onChange={e => setVoterName(e.target.value)}
                  required
                />
              </div>
              <div className="scheduling-form-group">
                <label>Your Email *</label>
                <input
                  type="email"
                  value={voterEmail}
                  onChange={e => setVoterEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="scheduling-vote-grid">
              <div className="scheduling-vote-header">
                <div>Time Options</div>
                <div>Yes</div>
                <div>Maybe</div>
                <div>No</div>
                <div>Votes</div>
              </div>

              {poll.proposedTimes.map((time: string) => {
                const summary = voteSummary[time] || { yes: 0, maybe: 0, no: 0 };
                return (
                  <div key={time} className="scheduling-vote-row">
                    <div className="scheduling-vote-time">
                      {formatDateTime(time)}
                    </div>
                    <div>
                      <input
                        type="radio"
                        name={`vote-${time}`}
                        checked={votes[time] === 'yes'}
                        onChange={() => setVotes({ ...votes, [time]: 'yes' })}
                      />
                    </div>
                    <div>
                      <input
                        type="radio"
                        name={`vote-${time}`}
                        checked={votes[time] === 'maybe'}
                        onChange={() => setVotes({ ...votes, [time]: 'maybe' })}
                      />
                    </div>
                    <div>
                      <input
                        type="radio"
                        name={`vote-${time}`}
                        checked={votes[time] === 'no'}
                        onChange={() => setVotes({ ...votes, [time]: 'no' })}
                      />
                    </div>
                    <div className="scheduling-vote-count">
                      <span className="vote-yes">{summary.yes}</span> /
                      <span className="vote-maybe">{summary.maybe}</span> /
                      <span className="vote-no">{summary.no}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {poll.allowComments && (
              <div className="scheduling-form-group">
                <label>Comment (optional)</label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Add a note..."
                  rows={2}
                />
              </div>
            )}

            <button
              type="submit"
              className="scheduling-btn-primary scheduling-btn-full"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Vote'}
            </button>
          </form>
        )}

        {voters.length > 0 && (
          <div className="scheduling-voters-list">
            <h3>Participants ({voters.length})</h3>
            {voters.map((voter, idx) => (
              <div key={idx} className="scheduling-voter">
                <span>{voter.name}</span>
                {voter.comment && <p className="voter-comment">"{voter.comment}"</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingPolls;
